/**
 * New Year tee designs — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const newYearTeeDesignsPack: ToilePack = {
    slug: 'new-year-tee-designs',
    category: 'season',
    name: 'New Year tee designs',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'slogan', label: 'Cheers slogan', subject: 'The words "CHEERS TO THE NEW YEAR" lettered in bold retro type with stars, isolated on a plain ground, no shirt'},
        {key: 'bottle', label: 'Champagne bottle', subject: 'A popping champagne bottle with a burst of bubbles, isolated on a plain ground, no shirt'},
        {key: 'sunburst', label: 'Retro sunburst', subject: 'A retro sunburst circle with a blank band across the middle for the year, distressed texture, isolated, no shirt'},
    ],
    styles: [
        {key: 'deco', label: 'Art deco', style: 'art deco illustration, geometric fans and sunbursts, clean lines, no outlines', palette: 'black and gold with a little cream', framing: 'Centered on a solid black background with an even margin'},
        {key: 'neon', label: 'Neon party', style: 'neon party graphic, glowing lines, confetti and streamers', palette: 'electric pink, cyan and gold on deep navy', framing: 'Centered on a dark background, built to read clean at small size'},
        {key: 'minimal', label: 'Minimal', style: 'modern minimal illustration, few flat shapes, generous space', palette: 'gold and white with a single black', framing: 'Centered on a plain white background with generous space around it'},
    ],
    directions: ['for a kids shirt', 'in a single colour', 'as a pocket-size print'],
    directionHint: 'try: for a kids shirt',
    seo: {
        title: 'New Year Shirt Design: Cheers, Bottle, Sunburst',
        metaDescription: 'Nine new year t-shirt designs — cheers slogan, champagne bottle, retro sunburst with a blank year band — isolated, three styles.',
        h1: 'New year shirt design: cheers, bottle, sunburst',
        tagline: 'Nine designs, three midnight subjects, three styles each.',
        intro: 'Print-ready chest art for the countdown shirt: a lettered cheers slogan, a champagne bottle, a retro sunburst with the year band left blank. Ask for a resolution-themed line, a countdown numeral, or your own slogan, and the same three styles carry it.',
        useCases: [
            "a New Year's Eve party t-shirt",
            'a matching family or group shirt run',
            'a print-on-demand shop listing',
            'a gift shirt for the new year',
        ],
        faq: [
            {q: 'What does this pack actually look like?', a: 'Nine chest-art designs — a cheers slogan, a champagne bottle, a retro sunburst — isolated with no shirt mockup, in three styles.'},
            {q: 'Do I get a shirt mockup, or just the artwork?', a: 'Just the artwork, isolated and ready to place — no shirt is rendered, so it drops onto any shirt color or cut.'},
            {q: 'Can I add my own year or slogan to the sunburst?', a: 'The band is left blank on purpose; adding your own year or short line on top is a quick edit after downloading.'},
            {q: 'Is the file good enough for screen printing or DTG?', a: "Each design renders at print resolution — check your printer's exact file requirements before sending it to production."},
            {q: 'Can I sell shirts using this design?', a: 'Yes — check the license terms on your account for the specifics of commercial use.'},
        ],
    },
}
